import React from 'react';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { Box, Icon, Menu, Portal, ScrollArea, Text } from '@chakra-ui/react';
import { LuArchive, LuMapPin } from 'react-icons/lu';
import PropertySelectorGroup from './PropertySelectorGroup.tsx';
import PropertySelectorGroupAdd from './PropertySelectorGroupAdd.tsx';
import PropertySelectorSearch from './PropertySelectorSearch.tsx';

import { usePropertySelectorModel } from './usePropertySelectorModel.ts';
import PropertySelectorChevrons from './PropertySelectorChevrons';
import SavedCardPaymentConfirmationDialog from '@/components/payment/SavedCardPaymentConfirmationDialog.tsx';
import { useHouseContext } from '@/context/HouseContext.tsx';
import { isOwnedArchiveCopy } from '@/utils/properties/propertyGroupingUtils';
import { useAuth } from '@/context/AuthContext';
import PropertyCopyTag from '@/components/properties/PropertyCopyTag';
import { hideNativeScrollbarCss } from '@/components/ui/scrollbar.ts';
import {
	PROPERTY_SELECTOR_DESKTOP_EDGE_INSET,
	PROPERTY_SELECTOR_DESKTOP_SURFACE_INSET,
} from './propertySelectorSections.ts';

const PROPERTY_SELECTOR_CONTENT_INSET = PROPERTY_SELECTOR_DESKTOP_SURFACE_INSET + PROPERTY_SELECTOR_DESKTOP_EDGE_INSET;

interface PropertySelectorDesktopProps {
	unreadNotificationPropertyIds: Set<number>;
	hasUnreadElsewhere: boolean;
}

/**
 * Desktop-only property selector component using Menu dropdown
 * Displays properties organized by categories with search functionality
 */
const PropertySelectorDesktop: React.FC<PropertySelectorDesktopProps> = ({
	unreadNotificationPropertyIds,
	hasUnreadElsewhere,
}) => {
	const {
		formattedAddress,
		totalProperties,
		searchQuery,
		setSearchQuery,
		showSearch,
		groups,
		selectedHouseId,
		handleHouseSelect,
		startSubscribeCheckout,
		isSubscribeCheckingOut,
		isSubscribeConfirmationOpen,
		subscribeSavedCard,
		subscribePendingAmountCents,
		confirmSubscribeCheckout,
		cancelSubscribeCheckout,
		isHouseLocked,
		transferringPropertyIds,
		canAddNewProperties,
		startAddProperty,
		confirmCheckout,
		cancelCheckout,
		isConfirmationOpen,
		savedCard,
		pendingPayment,
		isCheckingOut,
		canStartFlow,
	} = usePropertySelectorModel();
	const { selectedHouse } = useHouseContext();
	const { user } = useAuth();
	return (
		<>
			<Menu.Root>
				<Menu.Trigger asChild p={{ base: 4, md: 3 }} mt={{ base: 0, md: 1 }}>
					<OutlinedButton
						size="md"
						fontWeight="medium"
						maxWidth={{ base: '300px', md: '600px' }}
						border="none"
						bg="transparent"
						_hover={{ bg: 'surface.subtle' }}
					>
						<Icon
							as={selectedHouse && isOwnedArchiveCopy(selectedHouse, user?.id) ? LuArchive : LuMapPin}
							boxSize={5}
						/>
						<Text fontSize="md" truncate>
							{formattedAddress}
						</Text>
						<PropertyCopyTag isCopy={selectedHouse?.isCopy} />
						<PropertySelectorChevrons hasUnreadElsewhere={hasUnreadElsewhere} />
					</OutlinedButton>
				</Menu.Trigger>
				<Portal>
					<Menu.Positioner>
						<Menu.Content
							p={0}
							w="360px"
							maxW="calc(100vw - 32px)"
							maxH="min(76vh, 640px)"
							overflow="hidden"
							display="flex"
							flexDirection="column"
							borderRadius={30}
							bg="surface.default"
							border="1px solid"
							borderColor="border.default"
							boxShadow="lg"
						>
							{showSearch && (
								<Box
									flexShrink={0}
									px={PROPERTY_SELECTOR_CONTENT_INSET}
									pt={2}
									pb={1}
									borderBottomWidth="1px"
									borderColor="border.default"
								>
									<PropertySelectorSearch
										totalProperties={totalProperties}
										searchQuery={searchQuery}
										onSearchChange={setSearchQuery}
									/>
								</Box>
							)}

							<ScrollArea.Root flex={1} minH={0} size="xs" variant="always">
								<ScrollArea.Viewport h="full" css={hideNativeScrollbarCss}>
									<ScrollArea.Content px={PROPERTY_SELECTOR_CONTENT_INSET} py={2}>
										{groups.map((group) => (
											<PropertySelectorGroup
												key={group.key}
												groupKey={group.key}
												title={group.title}
												houses={group.houses}
												lockedCount={group.lockedCount}
												selectedHouseId={selectedHouseId}
												onHouseSelect={handleHouseSelect}
												isHouseLocked={isHouseLocked}
												onSubscribe={startSubscribeCheckout}
												isSubscribeLoading={isSubscribeCheckingOut}
												transferringPropertyIds={transferringPropertyIds}
												unreadNotificationPropertyIds={unreadNotificationPropertyIds}
											/>
										))}
									</ScrollArea.Content>
								</ScrollArea.Viewport>
								<ScrollArea.Scrollbar
									position="absolute"
									insetBlock={1}
									right={1}
									m={0}
									zIndex={1}
									bg="transparent"
								/>
							</ScrollArea.Root>

							{canAddNewProperties && (
								<Box
									flexShrink={0}
									px={PROPERTY_SELECTOR_CONTENT_INSET}
									pt={2.5}
									pb={3}
									borderTopWidth={groups.length > 0 ? '1px' : '0'}
									borderColor="border.default"
								>
									<PropertySelectorGroupAdd
										onAddProperty={startAddProperty}
										disabled={!canStartFlow}
										loading={isCheckingOut}
									/>
								</Box>
							)}
						</Menu.Content>
					</Menu.Positioner>
				</Portal>
			</Menu.Root>

			<SavedCardPaymentConfirmationDialog
				isOpen={isConfirmationOpen}
				amountCents={pendingPayment?.expected_price_cents ?? 0}
				card={savedCard}
				isLoading={isCheckingOut}
				onCancel={cancelCheckout}
				onConfirm={confirmCheckout}
			/>

			<SavedCardPaymentConfirmationDialog
				title="Activate Pro Subscription?"
				isOpen={isSubscribeConfirmationOpen}
				amountCents={subscribePendingAmountCents}
				card={subscribeSavedCard}
				isLoading={isSubscribeCheckingOut}
				onCancel={cancelSubscribeCheckout}
				onConfirm={confirmSubscribeCheckout}
			/>
		</>
	);
};

export default PropertySelectorDesktop;
